/**
 * Little-endian binary writer that mirrors LiteNetLib's NetDataWriter exactly, so the bytes it
 * produces are byte-identical to what the C# client/server write.
 *
 * Reference: Client/Assets/LiteNetLib/Utils/NetDataWriter.cs
 *  - All multi-byte numbers are little-endian (FastBitConverter).
 *  - bool  = 1 byte (0/1)
 *  - string: empty/null -> ushort 0; otherwise ushort(utf8ByteLen + 1) then the UTF-8 bytes.
 *  - arrays: ushort element-count, then packed little-endian elements.
 *  - long/ulong are 64-bit -> use BigInt (JS numbers lose precision past 2^53).
 */
export class BinaryWriter {
  private buf: Uint8Array;
  private view: DataView;
  private pos = 0;

  private static readonly encoder = new TextEncoder();
  /** Matches NetDataWriter.StringBufferMaxLength. */
  private static readonly STRING_BUFFER_MAX = 65535;

  constructor(initialSize = 64) {
    this.buf = new Uint8Array(initialSize);
    this.view = new DataView(this.buf.buffer);
  }

  private ensure(extra: number): void {
    const need = this.pos + extra;
    if (need <= this.buf.length) return;
    let next = this.buf.length * 2;
    while (next < need) next *= 2;
    const grown = new Uint8Array(next);
    grown.set(this.buf.subarray(0, this.pos));
    this.buf = grown;
    this.view = new DataView(this.buf.buffer);
  }

  putByte(value: number): this {
    this.ensure(1);
    this.view.setUint8(this.pos, value & 0xff);
    this.pos += 1;
    return this;
  }

  putSByte(value: number): this {
    this.ensure(1);
    this.view.setInt8(this.pos, value);
    this.pos += 1;
    return this;
  }

  putBool(value: boolean): this {
    return this.putByte(value ? 1 : 0);
  }

  putShort(value: number): this {
    this.ensure(2);
    this.view.setInt16(this.pos, value, true);
    this.pos += 2;
    return this;
  }

  putUShort(value: number): this {
    this.ensure(2);
    this.view.setUint16(this.pos, value, true);
    this.pos += 2;
    return this;
  }

  putInt(value: number): this {
    this.ensure(4);
    this.view.setInt32(this.pos, value, true);
    this.pos += 4;
    return this;
  }

  putUInt(value: number): this {
    this.ensure(4);
    this.view.setUint32(this.pos, value, true);
    this.pos += 4;
    return this;
  }

  putLong(value: bigint): this {
    this.ensure(8);
    this.view.setBigInt64(this.pos, value, true);
    this.pos += 8;
    return this;
  }

  putULong(value: bigint): this {
    this.ensure(8);
    this.view.setBigUint64(this.pos, value, true);
    this.pos += 8;
    return this;
  }

  putFloat(value: number): this {
    this.ensure(4);
    this.view.setFloat32(this.pos, value, true);
    this.pos += 4;
    return this;
  }

  putDouble(value: number): this {
    this.ensure(8);
    this.view.setFloat64(this.pos, value, true);
    this.pos += 8;
    return this;
  }

  putBytes(bytes: Uint8Array): this {
    this.ensure(bytes.length);
    this.buf.set(bytes, this.pos);
    this.pos += bytes.length;
    return this;
  }

  /**
   * Mirrors NetDataWriter.Put(string): ushort(byteLen + 1) prefix then UTF-8 bytes, or ushort 0 for
   * null/empty. Note the deliberate "+1" — a prefix of 0 means empty, 1 would mean a zero-length body.
   */
  putString(value: string | null | undefined): this {
    if (value === null || value === undefined || value.length === 0) {
      return this.putUShort(0);
    }
    const bytes = BinaryWriter.encoder.encode(value);
    if (bytes.length === 0 || bytes.length >= BinaryWriter.STRING_BUFFER_MAX) {
      return this.putUShort(0);
    }
    this.putUShort(bytes.length + 1);
    return this.putBytes(bytes);
  }

  putUShortArray(values: readonly number[]): this {
    this.putUShort(values.length);
    for (const v of values) this.putUShort(v);
    return this;
  }

  putIntArray(values: readonly number[]): this {
    this.putUShort(values.length);
    for (const v of values) this.putInt(v);
    return this;
  }

  putStringArray(values: readonly string[]): this {
    this.putUShort(values.length);
    for (const v of values) this.putString(v);
    return this;
  }

  /** Number of bytes written so far. */
  get length(): number {
    return this.pos;
  }

  /** Returns a copy of exactly the bytes written. */
  toUint8Array(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }
}
